import type { ComponentPropsWithoutRef, FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import type { ImageSource } from '../../../types'
import { ImageCaption } from '../../Image/ImageCaption'
import { ImageCredit } from '../../Image/ImageCredit'
import type { AspectRatio } from '../Picture'
import { Picture } from '../Picture'
import styles from './PictureCaptioned.module.scss'

export const PictureCaptioned: FunctionComponent<Props> = ({
  aspectRatio,
  src,
  sources,
  alt = '',
  credit,
  caption,
  wrapperClassName,
  imageClassName,
  width,
  height,
  lazyLoad,
  ...rest
}) => {
  return (
    <>
      <figure
        data-testid="figure-wrapper"
        className={classnames(styles.figureWrapper, wrapperClassName)}
        {...rest}
      >
        <div className={styles.imageCaptionWrapper}>
          <Picture
            alt={alt}
            aspectRatioMain={aspectRatio}
            src={src}
            sources={sources}
            imageClassName={imageClassName}
            width={width}
            height={height}
            lazyLoad={lazyLoad}
          />

          {credit && (
            <ImageCredit
              className={classnames(
                styles.imageElementCreditImage,
                caption
                  ? styles.imageElementCreditWithCaption
                  : styles.imageElementCredit
              )}
              {...{ credit }}
            />
          )}
        </div>
        {caption && (
          <ImageCaption
            className={classnames(
              'sb-margin-top-1',
              'sb-margin-bottom-2',
              styles.imageElementCaption
            )}
            {...{ caption }}
          />
        )}
      </figure>
    </>
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Aspect Ratio to be applied to the image box
   */
  aspectRatio?: AspectRatio
  /**
   * Alt text to be displayed for the image
   */
  alt?: string
  /**
   * Credit to external photographers if required, appears in bottom right corner of image
   */
  credit?: string
  /**
   * Optional caption to be displayed under the image
   */
  caption?: string
  /**
   * Class name that can be applied specifically to the image for extra styling purposes
   */
  imageClassName?: string
  /**
   * Source link to image to be displayed. NB This image must be in jpeg or gif format.
   */
  src: string
  /**
   * Array of image sources that can be rendered for resonsive breakpoints.
   */
  sources?: ImageSource[]
  /**
   * Class name that can be applied to the image outer wrapper for extra styling purposes
   */
  wrapperClassName?: string
  /**
   * Value of the image width
   */
  width?: string
  /**
   * Value of the image height
   */
  height?: string
  /**
   * Boolean to control if image should be lazy loaded
   */
  lazyLoad?: boolean
} & ComponentPropsWithoutRef<'figure'>
